import React from "react";

const urlPattern = /^(https?:\/\/|mailto:)/i;

const parseInlineMarkdown = (text, keyPrefix = "inline") => {
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|\*[^*]+\*)/g;
  const nodes = [];
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const token = match[0];
    const key = `${keyPrefix}-${match.index}`;

    if (token.startsWith("**")) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`")) {
      nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith("[")) {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const label = linkMatch?.[1] || token;
      const href = linkMatch?.[2] || "";

      nodes.push(
        urlPattern.test(href) ? (
          <a key={key} href={href} target="_blank" rel="noreferrer">
            {label}
          </a>
        ) : (
          label
        )
      );
    } else if (token.startsWith("*")) {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.flatMap((node, index) => {
    if (typeof node !== "string") return node;

    return node.split("\n").flatMap((part, partIndex, parts) =>
      partIndex === parts.length - 1
        ? [part]
        : [part, <br key={`${keyPrefix}-br-${index}-${partIndex}`} />]
    );
  });
};

const MarkdownText = ({ children, className = "" }) => {
  const text = String(children || "");
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let paragraph = [];
  let list = null;

  const flushParagraph = () => {
    if (!paragraph.length) return;

    blocks.push({
      type: "paragraph",
      text: paragraph.join("\n"),
    });
    paragraph = [];
  };

  const flushList = () => {
    if (!list) return;

    blocks.push(list);
    list = null;
  };

  lines.forEach((line) => {
    const unorderedMatch = line.match(/^\s*[-*]\s+(.+)$/);
    const orderedMatch = line.match(/^\s*\d+\.\s+(.+)$/);

    if (!line.trim()) {
      flushParagraph();
      flushList();
      return;
    }

    if (unorderedMatch || orderedMatch) {
      flushParagraph();
      const type = unorderedMatch ? "ul" : "ol";
      const value = unorderedMatch?.[1] || orderedMatch?.[1] || "";

      if (!list || list.type !== type) {
        flushList();
        list = { type, items: [] };
      }

      list.items.push(value);
      return;
    }

    flushList();
    paragraph.push(line);
  });

  flushParagraph();
  flushList();

  if (!blocks.length) return null;

  return (
    <div className={`markdown-text ${className}`.trim()}>
      {blocks.map((block, blockIndex) => {
        if (block.type === "ul" || block.type === "ol") {
          const ListTag = block.type;

          return (
            <ListTag key={blockIndex} className="mb-0 ps-3">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>
                  {parseInlineMarkdown(item, `block-${blockIndex}-${itemIndex}`)}
                </li>
              ))}
            </ListTag>
          );
        }

        return (
          <p key={blockIndex} className="mb-0">
            {parseInlineMarkdown(block.text, `block-${blockIndex}`)}
          </p>
        );
      })}
    </div>
  );
};

export default MarkdownText;
